/**
 * "See it on your wall": any element with [data-ar-src] (a GLB link from the Yugal API) opens a
 * dialog with Google's <model-viewer>. On a phone its button places the canvas on a real wall at
 * true size: Scene Viewer or WebXR on Android, Quick Look on iPhone (USDZ made on the fly). On a
 * computer it shows the canvas in 3D and suggests opening the page on a phone.
 * Strings come from snippets/yugal-ar.liquid.
 */
(() => {
  if (window.YugalAR) return;
  const MODEL_VIEWER = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
  let loading = null;
  let dialog = null;

  const strings = () => {
    try {
      return JSON.parse(document.getElementById('yugal-ar-strings')?.textContent || '{}');
    } catch {
      return {};
    }
  };

  function loadViewer() {
    if (customElements.get('model-viewer')) return Promise.resolve();
    loading ||= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = MODEL_VIEWER;
      script.onload = () => customElements.whenDefined('model-viewer').then(resolve);
      script.onerror = () => {
        loading = null;
        reject(new Error('model-viewer failed to load'));
      };
      document.head.append(script);
    });
    return loading;
  }

  function build(t) {
    const el = document.createElement('dialog');
    el.className = 'y-ar';
    el.setAttribute('aria-label', t.title || 'On your wall');
    el.innerHTML = `
      <div class="y-ar__frame">
        <button type="button" class="y-ar__close" data-ar-close aria-label=""><span aria-hidden="true">×</span></button>
        <p class="y-eyebrow y-ar__title"></p>
        <div class="y-ar__stage" data-ar-stage><p class="y-meta y-ar__loading"></p></div>
        <p class="y-meta y-ar__hint" data-ar-hint></p>
      </div>`;
    el.querySelector('[data-ar-close]').setAttribute('aria-label', t.close || 'Close');
    el.querySelector('.y-ar__title').textContent = t.title || 'On your wall';
    el.querySelector('.y-ar__loading').textContent = t.loading || 'Hanging the canvas…';
    el.addEventListener('click', (event) => {
      if (event.target === el || event.target.closest('[data-ar-close]')) el.close();
    });
    el.addEventListener('close', () => {
      el.querySelector('model-viewer')?.remove();
      document.documentElement.style.removeProperty('overflow');
    });
    document.body.append(el);
    return el;
  }

  async function open(src, alt = '') {
    const t = strings();
    dialog ||= build(t);
    const stage = dialog.querySelector('[data-ar-stage]');
    const hint = dialog.querySelector('[data-ar-hint]');
    hint.textContent = t.hint || '';
    stage.querySelector('model-viewer')?.remove();
    stage.querySelector('.y-ar__loading').hidden = false;
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
    try {
      await loadViewer();
    } catch {
      stage.querySelector('.y-ar__loading').textContent = t.error || 'This view could not load. Please try again.';
      return;
    }
    const viewer = document.createElement('model-viewer');
    Object.entries({
      src,
      alt,
      ar: '',
      'ar-modes': 'webxr scene-viewer quick-look',
      'ar-placement': 'wall',
      'ar-scale': 'fixed',
      'camera-controls': '',
      'touch-action': 'pan-y',
      'interaction-prompt': 'none',
      'camera-orbit': '-18deg 82deg auto',
      'shadow-intensity': '0.7',
      exposure: '1.05',
      'environment-image': 'neutral',
      loading: 'eager',
    }).forEach(([key, value]) => viewer.setAttribute(key, value));
    const button = document.createElement('button');
    button.type = 'button';
    button.slot = 'ar-button';
    button.className = 'y-btn y-ar__place';
    button.textContent = t.place || 'Place it on your wall';
    viewer.append(button);
    viewer.addEventListener('load', () => {
      stage.querySelector('.y-ar__loading').hidden = true;
      if (!viewer.canActivateAR) hint.textContent = t.desktop || '';
    });
    viewer.addEventListener('error', () => {
      stage.querySelector('.y-ar__loading').textContent = t.error || 'This view could not load. Please try again.';
    });
    viewer.addEventListener('ar-status', (event) => {
      if (event.detail.status === 'failed') hint.textContent = t.failed || '';
    });
    stage.append(viewer);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-ar-src]');
    if (!trigger || !trigger.dataset.arSrc) return;
    event.preventDefault();
    open(trigger.dataset.arSrc, trigger.dataset.arAlt || '');
  });

  window.YugalAR = { open };
})();
